import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { cx } from '@emotion/css';
import { useEntitlements } from '@gm-commercial/entitlements-client';
import {
  usePermissions,
  useProfile,
  useRole,
} from '@gm-commercial/profile-context';
import {
  AlertPermission,
  ProfilePermission,
  Role,
} from '@gm-commercial/profile-model';
import AppBar, { type AppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';

import { SecureWrapper } from '~/common/components';
import { BackToHomeLink } from '~/common/components/BackToHomeLink/BackToHomeLink';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { ADMIN_ROLES } from '~/common/constants/common.constant';
import useActingProfile from '~/common/hooks/useActingProfile';
import {
  CI_DEMO_FLAG,
  FAQS_PAGE_FLAG,
} from '~/common/models/featureFlags.model';
import { NOTIFICATIONS_REQUIRED_ROLES } from '~/common/models/notification.model';
import {
  ACCOUNT_ORG_DETAILS,
  ACCOUNT_PROFILE_PERSONAL_PATH,
} from '~/common/models/pages/accountPages.model';
import { SETTINGS_NOTIFICATIONS_ACCOUNT } from '~/common/models/pages/settingsPages.model';
import {
  SUPPORT_CONTACT_PATH,
  SUPPORT_FAQS_PATH,
} from '~/common/models/pages/supportPages.model';
import { ProductCodes } from '~/common/models/plans.model';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  getDefaultRoutePath,
  getHeaderDisabledPaths,
  getProfileDisabled,
} from '~/common/utils/route/route.utils';
import { getNotificationNavBarItems } from '~/features/navigationDrawer/config/account/notification.navBarItems.config';
import { getSettingsNavBarItems } from '~/features/navigationDrawer/config/account/setting.navBarItems.config';
import { resolveShowTelematicsFleetNavSurfaces } from '~/features/navigationDrawer/fleetTelematicsNavSurfaces.utils';
import { useEmerNotificationsAccess } from '~/routes/settings/hooks/useEmerNotificationsAccess';
import {
  selectActingPermissionsContextIds,
  selectActingProfileFirstName,
  selectActingProfileId,
  selectActingProfileLastName,
  selectActingProfileOrganization,
  selectActingProfileStatus,
  selectActingRole,
} from '~/store/profile/profileSlice.selectors';
import {
  NAV_DRAWER_LARGE_UP_CLOSED_WIDTH,
  NAV_DRAWER_OPEN_WIDTH,
} from '../navigationDrawer/GlobalNavDrawer.styles';
import GlobalSearch from './components/GlobalSearch/GlobalSearch';
import NotificationBadge from './components/NotificationBadge/NotificationBadge';
import ProfileMenu from './components/ProfileMenu/ProfileMenu';
import { getDefaultProfileMenu } from './components/ProfileMenu/ProfileMenu.config';
import ScopedUserBanner from './components/scopedUserBanner/ScopedUserBanner';
import useStyles from './GlobalHeader.styles';

const selectActingProfileParams = createSelector(
  selectActingProfileId,
  selectActingProfileFirstName,
  selectActingProfileLastName,
  selectActingRole,
  selectActingProfileStatus,
  selectActingProfileOrganization,
  selectActingPermissionsContextIds,
  (usersId, firstName, lastName, role, status, organization, contextIds) => {
    return {
      usersId,
      firstName,
      lastName,
      role,
      status,
      organization,
      ...contextIds,
    };
  }
);

interface StyledAppBarProps extends AppBarProps {
  open?: boolean;
  isActing?: boolean;
  navDrawerHidden?: boolean;
}

export const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'isActing' && prop !== 'navDrawerHidden',
})<StyledAppBarProps>(({ theme, open, navDrawerHidden }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: navDrawerHidden
    ? '100%'
    : `calc(100% - (${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px))`,
  ...(open &&
    !navDrawerHidden && {
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      [theme.breakpoints.up('md')]: {
        marginLeft: `${NAV_DRAWER_OPEN_WIDTH}px`,
        width: `calc(100% - ${NAV_DRAWER_OPEN_WIDTH}px)`,
      },
    }),
}));

type GlobalHeaderProps = {
  /** Whether the navigation drawer is expanded. */
  navExpanded: boolean;
  /** Whether the header should span the space normally occupied by the drawer. */
  navDrawerHidden?: boolean;
};

const GlobalHeader = ({
  navExpanded,
  navDrawerHidden = false,
}: GlobalHeaderProps): JSX.Element => {
  const actingProfileParams = useSelector(selectActingProfileParams);
  const actingPermissionsContextIds = useSelector(
    selectActingPermissionsContextIds
  );
  const { isActing } = useActingProfile();

  const menuId = 'account-menu';
  const { classes } = useStyles();
  const theme = useTheme();
  // On mobile the scoped-user banner is rendered in the page content (below the
  // OnStar banner) instead of inside the header.
  const isAboveMobile = useMediaQuery(theme.breakpoints.up('md'));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { profile, actingProfile } = useProfile();
  const effectiveProfile = actingProfile ?? profile;
  const { hasRole } = useRole();
  // Entering an organization rewrites the acting role to account admin.
  const { hasRole: hasSignedInRole } = useRole(false);
  const { hasPermission } = usePermissions();
  const { hasProduct } = useEntitlements();
  const location = useLocation();

  const isMobileAppRequired = !!matchPath(
    AppRoutePaths.MOBILE_APP_REQUIRED,
    location.pathname
  );
  const profileMenuPassThrough =
    isMobileAppRequired ||
    !!matchPath(AppRoutePaths.SERVICE_UNAVAILABLE, location.pathname);
  const matchUnsupportedRoute = getHeaderDisabledPaths().some((path) =>
    matchPath(path, location.pathname)
  );
  const suppressNavigation = !!matchUnsupportedRoute;
  const unSpportedRoute = getProfileDisabled().some((path) =>
    matchPath(path, location.pathname)
  );

  const canUpdateProfile = useMemo(
    () => hasPermission([ProfilePermission.UPDATE]),
    [hasPermission]
  );

  const disableProfile = useMemo(() => !!unSpportedRoute, [unSpportedRoute]);
  const showBackToHomeLink =
    isMobileAppRequired &&
    !!actingProfileParams.role &&
    actingProfileParams.role !== Role.OPERATOR;

  const defaultRoutePath = useMemo(
    () =>
      actingProfileParams.role
        ? getDefaultRoutePath(
            actingProfileParams.role,
            {
              organizationsId: actingProfileParams.organizationsId,
              hubsId: actingProfileParams.hubsId,
              fleetsId: actingProfileParams.fleetsId,
            },
            effectiveProfile
          )
        : AppRoutePaths.ROOT,
    [actingProfileParams, effectiveProfile]
  );

  const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // 'Support' feature flag
  const { getFlag } = useFeatureFlags();
  const isFAQSupportFlagEnabled = getFlag(FAQS_PAGE_FLAG);
  const isCiDemoEnabled = Boolean(getFlag(CI_DEMO_FLAG));
  const hasTelematicsProductPlan = hasProduct(ProductCodes.OST);
  const hasOneFleetProductPlan = hasProduct(ProductCodes.OSOF);

  const showTelematicsFleetNavSurfaces = useMemo(
    () =>
      resolveShowTelematicsFleetNavSurfaces({
        getFlag,
        hasTelematicsProductPlan:
          hasTelematicsProductPlan || hasOneFleetProductPlan,
      }),
    [getFlag, hasTelematicsProductPlan, hasOneFleetProductPlan]
  );

  const { hasEmerAccess } = useEmerNotificationsAccess();
  const accountNavNotificationItems = useMemo(
    () =>
      getNotificationNavBarItems({
        profileParams: actingPermissionsContextIds,
        findFlagScope: getFlag,
        showTelematicsFleetNavSurfaces,
        hasEmerNotificationsAccess: hasEmerAccess,
      }),
    [
      actingPermissionsContextIds,
      getFlag,
      showTelematicsFleetNavSurfaces,
      hasEmerAccess,
    ]
  );

  const accountNavSettingsItems = useMemo(() => getSettingsNavBarItems(), []);

  const updatedProfileMenu = useMemo(() => {
    const { role, usersId } = actingProfileParams;
    const defaultProfileMenu = getDefaultProfileMenu();
    const isNotificationAllowed = hasSignedInRole(NOTIFICATIONS_REQUIRED_ROLES);
    const isIntervalUser = hasRole(ADMIN_ROLES);
    if (
      !usersId ||
      role === Role.OPERATOR ||
      suppressNavigation ||
      disableProfile ||
      !canUpdateProfile
    ) {
      return defaultProfileMenu;
    }

    const menu = [
      {
        label: 'common:header.profile',
        testId: 'account-profile-global-nav',
        onClick: { redirect: ACCOUNT_PROFILE_PERSONAL_PATH },
        section: 'top',
      },
      ...(!isIntervalUser
        ? [
            {
              label: 'common:organization',
              testId: 'account-organization-global-nav',
              onClick: { redirect: ACCOUNT_ORG_DETAILS },
              section: 'top',
            },
          ]
        : []),
      ...(isNotificationAllowed && accountNavNotificationItems.length > 0
        ? [
            {
              label: 'settings:notifications.title',
              testId: 'account-notification-global-nav',
              onClick: { redirect: SETTINGS_NOTIFICATIONS_ACCOUNT },
              section: 'top',
            },
          ]
        : []),
      ...(accountNavSettingsItems.length > 0
        ? [
            {
              label: 'common:header.settings',
              testId: 'account-settings-global-nav',
              onClick: {
                redirect: accountNavSettingsItems[0].path,
              },
              section: 'top',
            },
          ]
        : []),
      ...(isFAQSupportFlagEnabled
        ? [
            {
              label: 'common:header.support',
              testId: 'account-support-global-menu',
              onClick: {
                redirect: SUPPORT_FAQS_PATH,
              },
              section: 'top',
            },
          ]
        : [
            {
              label: 'common:header.support',
              testId: 'account-support-global-menu',
              onClick: { redirect: SUPPORT_CONTACT_PATH },
              section: 'top',
            },
          ]),
    ];

    return [...menu, ...defaultProfileMenu];
  }, [
    accountNavNotificationItems,
    accountNavSettingsItems,
    actingProfileParams,
    canUpdateProfile,
    disableProfile,
    hasRole,
    hasSignedInRole,
    isFAQSupportFlagEnabled,
    suppressNavigation,
  ]);

  return (
    <StyledAppBar
      className={cx(classes.headerWrapper, {
        hideBar: suppressNavigation,
        acting: isActing,
      })}
      position="fixed"
      open={navExpanded}
      isActing={isActing}
      navDrawerHidden={navDrawerHidden}
    >
      <Box className={cx(classes.headerInner, { acting: isActing })}>
        {isAboveMobile && <ScopedUserBanner />}
        <Box className={cx(classes.container, { acting: isActing })}>
          <Toolbar className={classes.header}>
            {showBackToHomeLink && (
              <BackToHomeLink
                to={defaultRoutePath}
                className={classes.backToHomeLink}
                testId="header-back-to-home-link"
              />
            )}
            {isCiDemoEnabled && (
              <span
                className={classes.ciDemoBadge}
                data-testid="ci-demo-header-badge"
              >
                CI/CD DEMO
              </span>
            )}
            {!isMobileAppRequired && (
              <SecureWrapper requiredPermissions={[]} passThrough>
                <GlobalSearch />
              </SecureWrapper>
            )}
            <SecureWrapper
              requiredPermissions={[AlertPermission.READ]}
              requiredRoles={NOTIFICATIONS_REQUIRED_ROLES}
              actingRole={false}
            >
              <NotificationBadge />
            </SecureWrapper>
            <SecureWrapper
              requiredPermissions={[]}
              passThrough={profileMenuPassThrough}
            >
              <div>
                <Button
                  id="user-menu-btn"
                  aria-label={translateText('common:header.openUserMenu')}
                  aria-controls={menuId}
                  aria-haspopup="true"
                  color="primary"
                  onClick={handleMenu}
                  className={classes.profileContainerButton}
                >
                  <div
                    className={cx(classes.profileContainer, {
                      isOpen: !!anchorEl,
                    })}
                    data-app-version={globalThis?.appConfig?.version}
                  >
                    {actingProfileParams?.firstName?.charAt(0)}
                    {actingProfileParams?.lastName?.charAt(0)}
                  </div>
                </Button>
                <ProfileMenu
                  label={translateText('common:header.profileMenu')}
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  items={updatedProfileMenu.map(
                    ({ label, testId, onClick, section }) => ({
                      label: translateText(label),
                      testId,
                      onClick: { redirect: onClick?.redirect },
                      section: section,
                    })
                  )}
                />
              </div>
            </SecureWrapper>
          </Toolbar>
        </Box>
      </Box>
    </StyledAppBar>
  );
};

export default GlobalHeader;
